/**
 * Parlay enumeration engine.
 *
 * Extracted from {@code parlay-builder.tsx} so the default value-finding
 * builder and the promo-mode builder share one source of truth for:
 *   • per-leg +EV candidate collection
 *   • combined parlay EV math
 *   • event-diverse DFS combo enumeration with bounded decimal odds
 *
 * Promo mode layers extra constraints on top of these primitives
 * (sport whitelist, per-leg odds floor/ceiling, min/max legs, allow
 * same event) — see {@code promos/promo-utils.ts}.
 */

import { PositiveEVRow, Bet } from '../positive-ev/types/positive-ev.types';
import { americanOddsToImpliedProbability } from '../positive-ev/services/positive-ev-service';
import { ParlayLeg, americanToDecimal } from './parlay-share-utils';

/* ─── Tunables ─── */

/** Hard upper bound on legs in any enumerated parlay. */
export const MAX_LEGS = 7;
export const MIN_LEGS = 2;

/** Cap on candidate legs considered when enumerating combos (top by EV%). */
export const ENUMERATE_CANDIDATE_CAP = 25;

/** Cap on number of distinct combos retained (for memory + weighted-pick perf). */
export const MAX_COMBOS = 5000;

/** Default minimum combined EV% required for a parlay to be considered. */
export const DEFAULT_MIN_PARLAY_EV_PCT = 1;

/* ─── Math helpers ─── */

/**
 * Compute the EV% for a specific bet given that bet's listed odds and the
 * market's fair odds. Uses the same formula as the backend
 * PositiveEVRowService.calculateEVPercentage.
 */
export function calcBetEV(listedOdds: number, fairOdds: number): number {
  const trueProb = americanOddsToImpliedProbability(fairOdds);
  if (!trueProb || trueProb <= 0) return -999;

  let profit: number;
  if (listedOdds > 0) {
    profit = listedOdds / 100;
  } else {
    profit = 100 / Math.abs(listedOdds);
  }

  const ev = (trueProb * profit) - ((1 - trueProb) * 1);
  return Math.round(ev * 10000) / 100;
}

export function combinedParlayEvPct(legs: ParlayLeg[]): number {
  if (!legs.length) return 0;
  const trueProb = legs.reduce((p, l) => {
    const q = americanOddsToImpliedProbability(l.fairOdds);
    return p * (q ?? 0);
  }, 1);
  const actualDec = legs.reduce((p, l) => p * americanToDecimal(l.odds), 1);
  return Math.round((trueProb * actualDec - 1) * 10000) / 100;
}

/** Product of fair-odds implied probabilities. Uses 0 for malformed legs. */
export function combinedParlayTrueProb(legs: ParlayLeg[]): number {
  if (!legs.length) return 0;
  return legs.reduce((p, l) => {
    const q = americanOddsToImpliedProbability(l.fairOdds);
    return p * (q ?? 0);
  }, 1);
}

export function combinedParlayDecimalOdds(legs: ParlayLeg[]): number {
  if (!legs.length) return 1;
  return legs.reduce((p, l) => p * americanToDecimal(l.odds), 1);
}

/* ─── Candidate collection ─── */

export interface CandidateOptions {
  /**
   * Restrict to legs whose `sport` (which is `row.league || row.sport`)
   * matches one of these keys. Empty / undefined = no restriction.
   */
  sportKeys?: readonly string[];
  /** Per-leg decimal odds floor (e.g. 1.4 for "-250 or longer"). */
  perLegDecimalMin?: number;
  /** Per-leg decimal odds ceiling. */
  perLegDecimalMax?: number;
  /**
   * Whether each leg's listed price must already be individually +EV
   * (i.e. above the market's fair odds). Defaults to {@code true} —
   * which is correct for the value-finding builder, where we only
   * want to surface legs the user is gaining edge on at face value.
   *
   * Promo mode passes {@code false}: a profit-boost token is the
   * source of edge, so legs slightly worse than fair at the promo
   * book can still combine into a positively-boosted parlay. Setting
   * this to false typically expands the DraftKings/BetMGM candidate
   * pool by 1-2 orders of magnitude (most legs at the major books
   * don't beat fair odds on their own).
   */
  requireIndividualEV?: boolean;
}

/**
 * Walk the +EV rows and emit one {@link ParlayLeg} per (event, market, pick)
 * tuple where the bet is on the requested book and is individually +EV.
 *
 * Optional options narrow the candidate pool by sport / per-leg odds —
 * used by promo mode to enforce the promo's eligibility rules at the
 * leg level before any combo enumeration runs.
 */
export function collectCandidates(
  rows: PositiveEVRow[],
  bookKey: string,
  options?: CandidateOptions
): ParlayLeg[] {
  const sportKeys = options?.sportKeys && options.sportKeys.length > 0
    ? new Set(options.sportKeys)
    : null;
  const minDec = options?.perLegDecimalMin;
  const maxDec = options?.perLegDecimalMax;
  const requireIndividualEV = options?.requireIndividualEV !== false;

  const candidates: ParlayLeg[] = [];
  const seen = new Set<string>();

  function tryAdd(bet: Bet, fairOdd: number, row: PositiveEVRow) {
    if (!bet.bookie || !bet.price || !fairOdd) return;
    if (!bet.bookie.toLowerCase().includes(bookKey.toLowerCase())) return;

    const legSport = row.league || row.sport || '';
    if (sportKeys && !sportKeys.has(legSport)) return;

    if (minDec != null || maxDec != null) {
      const dec = americanToDecimal(bet.price);
      if (minDec != null && dec < minDec) return;
      if (maxDec != null && dec > maxDec) return;
    }

    const betEV = calcBetEV(bet.price, fairOdd);
    // Default (value mode): require each leg to beat fair on its own.
    // Promo mode opts out so the boost can rescue short-deficit legs.
    if (requireIndividualEV && betEV <= 0) return;

    const key = `${row.event}||${row.market}||${bet.name}`;
    if (seen.has(key)) return;
    seen.add(key);
    candidates.push({
      event: row.event,
      market: row.market,
      pick: bet.name,
      odds: bet.price,
      fairOdds: fairOdd,
      evPercent: betEV,
      bookie: bet.bookie,
      sport: legSport,
      eventDate: row.event_date || '',
      kalshiTicker: bet.kalshiTicker ?? null,
      netOdds: bet.netPrice ?? null,
      outcomeLink: bet.outcomeLink ?? null,
    });
  }

  for (const row of rows) {
    if (row.bets && row.bets.length > 0) {
      for (let i = 0; i < row.bets.length; i++) {
        const fairOdd = row.fair_odds?.[i] ?? 0;
        if (!fairOdd) continue;
        for (const bet of row.bets[i]) tryAdd(bet, fairOdd, row);
      }
    }
    if (row.fair_odds_home && row.home_bets?.length) {
      for (const bet of row.home_bets) tryAdd(bet, row.fair_odds_home, row);
    }
    if (row.fair_odds_away && row.away_bets?.length) {
      for (const bet of row.away_bets) tryAdd(bet, row.fair_odds_away, row);
    }
  }

  return candidates;
}

/* ─── Combo enumeration ─── */

export interface ParlayCombo {
  legs: ParlayLeg[];
  decimalOdds: number;
  evPct: number;
}

export interface EnumerateOptions {
  /** Lower bound on combined decimal odds (inclusive). */
  targetDecimal: number;
  /** Upper bound on combined decimal odds (inclusive). Defaults to Infinity. */
  maxDecimal?: number;
  /** Lower bound on legs. Defaults to {@link MIN_LEGS}. */
  minLegs?: number;
  /** Upper bound on legs. Defaults to {@link MAX_LEGS}. */
  maxLegs?: number;
  /** Lower bound on combined parlay EV% (exclusive). Defaults to {@link DEFAULT_MIN_PARLAY_EV_PCT}. */
  minComboEvPct?: number;
  /** Cap on candidates considered (top by individual EV%). Defaults to {@link ENUMERATE_CANDIDATE_CAP}. */
  candidateCap?: number;
  /**
   * When true, enumeration permits multiple legs from the same event
   * (SGP-style). Default false (event-diverse, the default builder
   * behavior).
   */
  allowSameEvent?: boolean;
}

/**
 * DFS enumeration of size-{@code minLegs..maxLegs} subsets of the supplied
 * candidate pool. A combo is recorded iff:
 *   - combined decimal odds in [targetDecimal, maxDecimal]
 *   - combined parlay EV% > minComboEvPct
 *   - (when {@code allowSameEvent} is false) all legs are from distinct events
 *
 * Pruning:
 *   - Skip combos that already used an event (when applicable).
 *   - Skip if combined product * (max possible additional product) < target.
 *   - Cap candidate pool to top candidateCap by individual EV%.
 *   - Stop recording past {@link MAX_COMBOS} to bound memory.
 */
export function enumerateParlayCombos(
  candidatesIn: ParlayLeg[],
  options: EnumerateOptions
): ParlayCombo[] {
  const minLegs = options.minLegs ?? MIN_LEGS;
  const maxLegs = options.maxLegs ?? MAX_LEGS;
  const minComboEvPct = options.minComboEvPct ?? DEFAULT_MIN_PARLAY_EV_PCT;
  const candidateCap = options.candidateCap ?? ENUMERATE_CANDIDATE_CAP;
  const targetDecimal = options.targetDecimal;
  const maxDecimal = options.maxDecimal ?? Infinity;
  const allowSameEvent = options.allowSameEvent === true;

  const candidates = candidatesIn
    .slice()
    .sort((a, b) => b.evPercent - a.evPercent)
    .slice(0, candidateCap);

  if (candidates.length < minLegs) return [];

  const decOf = (l: ParlayLeg) => americanToDecimal(l.odds);

  const decimalsFromIdx: number[][] = candidates.map((_, i) =>
    candidates.slice(i).map(decOf).sort((a, b) => b - a)
  );
  function suffixMaxProduct(startIdx: number, k: number): number {
    if (k <= 0) return 1;
    const arr = decimalsFromIdx[startIdx] ?? [];
    let prod = 1;
    for (let i = 0; i < Math.min(k, arr.length); i++) prod *= arr[i];
    return prod;
  }

  const out: ParlayCombo[] = [];
  const picked: ParlayLeg[] = [];
  const usedEvents = new Set<string>();

  function record(prodDec: number) {
    const ev = combinedParlayEvPct(picked);
    if (ev <= minComboEvPct) return;
    out.push({ legs: [...picked], decimalOdds: prodDec, evPct: ev });
  }

  function dfs(start: number, prodDec: number) {
    if (out.length >= MAX_COMBOS) return;
    if (
      picked.length >= minLegs &&
      prodDec >= targetDecimal &&
      prodDec <= maxDecimal
    ) {
      record(prodDec);
    }
    if (picked.length >= maxLegs) return;

    const slotsLeft = maxLegs - picked.length;
    for (let i = start; i < candidates.length; i++) {
      if (out.length >= MAX_COMBOS) return;
      const leg = candidates[i];
      if (!allowSameEvent && usedEvents.has(leg.event)) continue;

      const newProd = prodDec * decOf(leg);

      // Hard prune: if we've already overshot the upper bound, every leg we
      // add only makes the product larger (decimal odds are always > 1), so
      // the rest of this branch can never re-enter the valid window.
      if (newProd > maxDecimal) continue;

      // Upper bound: best we could ever reach from here.
      if (newProd < targetDecimal) {
        const ub = newProd * suffixMaxProduct(i + 1, slotsLeft - 1);
        if (ub < targetDecimal) continue;
      }

      if (!allowSameEvent) usedEvents.add(leg.event);
      picked.push(leg);
      dfs(i + 1, newProd);
      picked.pop();
      if (!allowSameEvent) usedEvents.delete(leg.event);
    }
  }

  dfs(0, 1);
  return out;
}

/* ─── Per-book max +EV-preserving combined return ─── */

/**
 * For a given book, compute the maximum combined decimal odds achievable
 * using only +EV candidate legs (one per event), capped at {@link MAX_LEGS},
 * while the combined parlay EV% remains above the threshold.
 *
 * Since every candidate leg is individually +EV, parlay EV% strictly
 * grows as legs are added (combined factor = prod(1 + ev_i)), so the
 * straightforward greedy "take top N highest-decimal legs (one per event)"
 * yields the true max while preserving the EV constraint in nearly all
 * realistic cases. We still defensively check the threshold at the end.
 */
export function computeMaxReturnForBook(
  candidates: ParlayLeg[],
  minComboEvPct: number = DEFAULT_MIN_PARLAY_EV_PCT
): number {
  if (candidates.length < MIN_LEGS) return 0;

  const byEvent = new Map<string, ParlayLeg>();
  for (const c of candidates) {
    const existing = byEvent.get(c.event);
    if (!existing || americanToDecimal(c.odds) > americanToDecimal(existing.odds)) {
      byEvent.set(c.event, c);
    }
  }

  const sorted = Array.from(byEvent.values()).sort(
    (a, b) => americanToDecimal(b.odds) - americanToDecimal(a.odds)
  );
  const top = sorted.slice(0, MAX_LEGS);
  if (top.length < MIN_LEGS) return 0;

  const ev = combinedParlayEvPct(top);
  if (ev <= minComboEvPct) return 0;

  return top.reduce((p, l) => p * americanToDecimal(l.odds), 1);
}

export function maxLegDecimal(combo: ParlayCombo): number {
  return combo.legs.reduce(
    (m, l) => Math.max(m, americanToDecimal(l.odds)),
    1
  );
}

export function legDecimalStdDev(combo: ParlayCombo): number {
  const ds = combo.legs.map((l) => americanToDecimal(l.odds));
  if (ds.length < 2) return 0;
  const mean = ds.reduce((s, d) => s + d, 0) / ds.length;
  const variance =
    ds.reduce((s, d) => s + (d - mean) ** 2, 0) / ds.length;
  return Math.sqrt(variance);
}
